import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '../context/CompareContext';
import { FaSpinner, FaTimes, FaChevronLeft } from 'react-icons/fa';

/**
 * Helper function to calculate 1-Year Return
 * It finds the latest NAV and compares it to the NAV from ~252 trading days ago
 */
function calculateReturn(navs) {
  if (!navs || navs.length < 252) {
    return 'N/A';
  }
  const latestNav = parseFloat(navs[0].nav);
  const yearAgoNav = parseFloat(navs[251].nav);
  if (yearAgoNav === 0) return 'N/A';

  const returnPercent = ((latestNav - yearAgoNav) / yearAgoNav) * 100;
  return returnPercent.toFixed(2) + '%';
}

function getLatestNav(navs) {
  if (!navs || navs.length === 0) return 'N/A';
  return '₹ ' + parseFloat(navs[0].nav).toFixed(2);
}

/**
 * Main Compare Page Component
 */
function ComparePage() {
  const { compareList, removeFundFromCompare } = useCompare();
  const [fundDetails, setFundDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // This effect re-runs whenever the compareList changes
  useEffect(() => {
    // If list is empty, do nothing
    if (compareList.length === 0) {
      setFundDetails([]); // Clear any old details
      return;
    }

    const fetchAllFundData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Create an array of fetch promises
        const promises = compareList.map(fund =>
          fetch(`https://api.mfapi.in/mf/${fund.schemeCode}`).then(res => {
            if (!res.ok) throw new Error(`Failed to fetch ${fund.schemeName}`);
            return res.json();
          })
        );
        
        // Wait for all promises to resolve
        const results = await Promise.all(promises);
        setFundDetails(results);
        
      } catch (err) {
        setError(err.message);
        setFundDetails([]); // Clear data on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllFundData();
  }, [compareList]); // Dependency array is key!

  
  // --- Render logic ---
  
  const renderEmptyState = () => (
    <div className="text-center p-12 bg-white shadow-lg rounded-xl">
      <h2 className="text-2xl font-bold text-gray-800">Your Compare List is Empty</h2>
      <p className="text-gray-600 mt-2">
        You haven't added any funds to compare yet.
      </p>
      <Link
        to="/mutual-funds"
        className="mt-6 inline-flex items-center py-2 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700"
      >
        <FaChevronLeft className="mr-2 h-4 w-4" />
        Go to Dashboard to Add Funds
      </Link>
    </div>
  );
  
  const renderLoading = () => (
    <div className="flex justify-center items-center p-12">
      <FaSpinner className="animate-spin text-blue-600 h-10 w-10" />
      <span className="ml-4 text-xl text-gray-700">Loading Comparison Data...</span>
    </div>
  );

  const renderTable = () => (
    <div className="bg-white shadow-xl rounded-xl overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        {/* --- Table Header (Fund Names) --- */}
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
              Feature
            </th>
            {fundDetails.map((fund) => (
              <th key={fund.meta.scheme_code} className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                <div className="flex justify-between items-start">
                  <Link to={`/mutual-funds/${fund.meta.scheme_code}`} className="text-blue-600 hover:text-blue-800">
                    {fund.meta.scheme_name}
                  </Link>
                  <button 
                    onClick={() => removeFundFromCompare(fund.meta.scheme_code)}
                    className="ml-2 text-gray-400 hover:text-red-500"
                    title="Remove from Compare"
                  >
                    <FaTimes />
                  </button>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        
        {/* --- Table Body (Fund Data) --- */}
        <tbody className="bg-white divide-y divide-gray-200">
          {/* Row: Fund House */}
          <tr>
            <td className="px-6 py-4 font-medium text-gray-800 sticky left-0 bg-white z-10">Fund House</td>
            {fundDetails.map(fund => (
              <td key={fund.meta.scheme_code} className="px-6 py-4 text-gray-700">{fund.meta.fund_house}</td>
            ))}
          </tr>
          
          {/* Row: Scheme Category */}
          <tr>
            <td className="px-6 py-4 font-medium text-gray-800 sticky left-0 bg-white z-10">Category</td>
            {fundDetails.map(fund => (
              <td key={fund.meta.scheme_code} className="px-6 py-4 text-gray-700">{fund.meta.scheme_category}</td>
            ))}
          </tr>

          {/* Row: Scheme Type */}
          <tr>
            <td className="px-6 py-4 font-medium text-gray-800 sticky left-0 bg-white z-10">Type</td>
            {fundDetails.map(fund => (
              <td key={fund.meta.scheme_code} className="px-6 py-4 text-gray-700">{fund.meta.scheme_type}</td>
            ))}
          </tr>

          {/* Row: Latest NAV */}
          <tr>
            <td className="px-6 py-4 font-medium text-gray-800 sticky left-0 bg-white z-10">Latest NAV</td>
            {fundDetails.map(fund => (
              <td key={fund.meta.scheme_code} className="px-6 py-4 text-gray-700 font-bold">{getLatestNav(fund.data)}</td>
            ))}
          </tr>

          {/* Row: 1-Year Return */}
          <tr>
            <td className="px-6 py-4 font-medium text-gray-800 sticky left-0 bg-white z-10">1-Year Return</td>
            {fundDetails.map(fund => (
              <td key={fund.meta.scheme_code} className="px-6 py-4 text-gray-700 font-bold">{calculateReturn(fund.data)}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          Compare Funds
        </h1>
        
        {/* Handle all the different states */}
        {isLoading
          ? renderLoading()
          : error
            ? <p className="text-center text-red-600">Error: {error}</p>
            : compareList.length === 0
              ? renderEmptyState()
              : renderTable()
        }
      </div>
    </div>
  );
}

export default ComparePage;